/**
 * SSOT for absolute public / customer-facing frontend URLs.
 * Never derive this from window.location or Vercel preview hosts.
 *
 * Env precedence (Vite):
 *   VITE_PUBLIC_SITE_BASE_URL > VITE_FRONTEND_PUBLIC_URL > VITE_BASE_URL
 * Dev fallback: http://localhost:8000
 * Production build without env: https://hotelsriver.com (last resort; set env in staging/prod)
 */

const PROD_FALLBACK = "https://hotelsriver.com";
const TRIPMASTER_PROD_FALLBACK = "https://www.tripmaster.co.il";
const DEV_FALLBACK = "http://localhost:8000";

let _traceOnce = false;

function trimBase(value) {
  if (value == null) return "";
  return String(value).trim().replace(/\/+$/, "");
}

function readEnvPair() {
  const keys = [
    "VITE_PUBLIC_SITE_BASE_URL",
    "VITE_FRONTEND_PUBLIC_URL",
    "VITE_BASE_URL",
  ];
  for (const k of keys) {
    const v = trimBase(import.meta.env[k]);
    if (v) return { url: v, source: k };
  }
  return { url: "", source: "" };
}

function resolveEnvironmentLabel() {
  const explicit = trimBase(import.meta.env.VITE_PUBLIC_ENV);
  if (explicit) return explicit;
  if (import.meta.env.DEV) return "local";
  return "production";
}

function normalizeToOrigin(url) {
  const raw = trimBase(url);
  if (!raw) return "";
  try {
    const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, "")}`;
    const u = new URL(href);
    return `${u.protocol}//${u.host}`;
  } catch {
    return "";
  }
}

function maybeDevTrace(resolvedBaseUrl, envSource) {
  if (_traceOnce) return;
  if (import.meta.env.VITE_SILENT_PUBLIC_BASE_LOG === "1") return;
  if (!import.meta.env.DEV) return;
  _traceOnce = true;
  const currentHost =
    typeof window !== "undefined" && window.location?.host ? window.location.host : "";
  console.info(
    "[PUBLIC_BASE_URL_TRACE]",
    JSON.stringify({
      currentHost,
      resolvedBaseUrl: resolvedBaseUrl,
      envSource,
      environment: resolveEnvironmentLabel(),
    })
  );
}

/**
 * Canonical origin for share links, public site, offers, emails (FE-side), og:url, etc.
 * @param {{ skipTrace?: boolean }} [options]
 * @returns {string}
 */
export function resolvePublicBaseUrl(options = {}) {
  const { skipTrace = false } = options;
  const { url, source } = readEnvPair();
  let envSource = source || "unset";
  let candidate = url;

  if (!candidate) {
    if (import.meta.env.DEV) {
      candidate = DEV_FALLBACK;
      envSource = "default_localhost_8000";
    } else if (String(import.meta.env.VITE_APP_SURFACE || "").trim().toLowerCase() === "tripmaster") {
      candidate = TRIPMASTER_PROD_FALLBACK;
      envSource = "fallback_tripmaster_bundle";
    } else {
      candidate = PROD_FALLBACK;
      envSource = "fallback_production_bundle";
    }
  }

  const resolved = normalizeToOrigin(candidate) || PROD_FALLBACK;
  if (!skipTrace) {
    maybeDevTrace(resolved, envSource);
  }
  return resolved;
}

/**
 * Current path on the canonical public base (og:url, meta canonical on preview).
 */
export function buildPublicPageCanonicalHref() {
  if (typeof window === "undefined") return "";
  const base = resolvePublicBaseUrl({ skipTrace: true });
  return `${base.replace(/\/$/, "")}${window.location.pathname || ""}${window.location.search || ""}`;
}

/**
 * Prefix a path or rewrite any http(s) URL to sit under the canonical public origin.
 * @param {string} pathOrUrl
 * @returns {string}
 */
export function absolutizePublicFrontendUrl(pathOrUrl) {
  const base = resolvePublicBaseUrl({ skipTrace: true }).replace(/\/$/, "");
  const raw = String(pathOrUrl || "").trim();
  if (!raw) return `${base}/`;
  if (raw.startsWith("/")) {
    return `${base}${raw}`;
  }
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const u = new URL(raw);
      return `${base}${u.pathname}${u.search || ""}${u.hash || ""}`;
    } catch {
      return raw;
    }
  }
  return `${base}/${raw.replace(/^\/+/, "")}`;
}
